import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { withLocalize } from 'react-localize-redux';
import {
    COLOR_MODES,
    defaultHeatmapOptions,
    HEATMAP_DEFAULT_BRACKET_COLOR,
    HEATMAP_NO_MATCH_COLOR,
    nextHeatmapColor,
    shouldShowValues,
} from '../../../customDashboards/heatmap/heatmapOptions';
import Button from '../../atoms/Button';
import Icon, { IconSizes, IconVariants } from '../../foundations/Icon';
import Dropdown from '../../molecules/Dropdown';
import TextField from '../../molecules/TextField';
import { Checkbox, Label } from '../../widgets';
import css from './HeatmapColorConfig.css';

const T = 'STATIC.PAGES.DASHBOARD.SHELF.HEATMAP';

const HeatmapColorConfig = ({ value = null, onChange, error = '', translate }) => {
    const MODE_OPTIONS = [
        { name: COLOR_MODES.GRADIENT, display_name: translate(`${T}.GRADIENT`) },
        { name: COLOR_MODES.BRACKETS, display_name: translate(`${T}.BRACKETS`) },
    ];

    const options = value || defaultHeatmapOptions();
    const mode = options.mode || COLOR_MODES.GRADIENT;
    const stops = options.stops || [];
    const dataClasses = options.dataClasses || [];

    const update = patch => onChange({ ...options, ...patch });

    const updateStop = (index, patch) => {
        update({ stops: stops.map((s, i) => (i === index ? { ...s, ...patch } : s)) });
    };
    const addStop = () =>
        update({ stops: [...stops, { stop: 1, color: nextHeatmapColor(stops.map(s => s.color)) }] });
    const removeStop = index => update({ stops: stops.filter((_, i) => i !== index) });

    const updateClass = (index, patch) => {
        update({ dataClasses: dataClasses.map((c, i) => (i === index ? { ...c, ...patch } : c)) });
    };
    const addClass = () =>
        update({
            dataClasses: [
                ...dataClasses,
                { from: undefined, to: undefined, color: nextHeatmapColor(dataClasses.map(c => c.color)), name: '' },
            ],
        });
    const removeClass = index => update({ dataClasses: dataClasses.filter((_, i) => i !== index) });

    return (
        <div>
            <div className={classNames(css.row, css.showValuesRow)}>
                <Checkbox
                    size="small"
                    checked={shouldShowValues(options)}
                    onChange={() => update({ showValues: options.showValues === false })}
                    label={translate(`${T}.SHOW_VALUES`)}
                />
            </div>
            <Label text={`${T}.COLOR_SCALE`} />
            <Dropdown
                items={MODE_OPTIONS}
                selected={MODE_OPTIONS.find(o => o.name === mode)}
                onSelection={opt => {
                    const nextMode = opt.name;
                    const patch = { mode: nextMode };
                    if (nextMode === COLOR_MODES.BRACKETS && dataClasses.length === 0) {
                        patch.dataClasses = [
                            { from: undefined, to: undefined, color: HEATMAP_DEFAULT_BRACKET_COLOR, name: '' },
                        ];
                    }
                    if (nextMode === COLOR_MODES.GRADIENT && stops.length === 0) {
                        patch.stops = defaultHeatmapOptions().stops;
                    }
                    update(patch);
                }}
                wrapperStyle={{ width: '100%' }}
                containerStyle={{ width: '100%' }}
            />
            {error && <div className={css.error}>{error}</div>}

            {mode === COLOR_MODES.GRADIENT &&
                stops.map((s, i) => (
                    // Editable rows have no stable id; index keys are acceptable for this small list.
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={`stop-${i}`} className={css.row}>
                        <div className={css.valueField}>
                            <TextField
                                placeholder={translate(`${T}.STOP_PLACEHOLDER`)}
                                value={s.stop === undefined || Number.isNaN(s.stop) ? '' : String(s.stop)}
                                onChange={v => {
                                    if (v === '') {
                                        updateStop(i, { stop: undefined });
                                        return;
                                    }
                                    const num = Number(v);
                                    if (Number.isNaN(num)) {
                                        return;
                                    }
                                    updateStop(i, { stop: Math.min(1, Math.max(0, num)) });
                                }}
                                inputConfig={{ type: 'number', step: 'any', min: 0, max: 1 }}
                            />
                        </div>
                        <input
                            type="color"
                            value={s.color}
                            onChange={e => updateStop(i, { color: e.target.value })}
                            className={css.colorSwatch}
                        />
                        <Icon
                            name="trash"
                            size={IconSizes.SMALL}
                            variant={IconVariants.DARK}
                            onClick={() => removeStop(i)}
                            aria-label={translate(`${T}.REMOVE_COLOR`)}
                        />
                    </div>
                ))}
            {mode === COLOR_MODES.GRADIENT && (
                <Button type="flat" Icon="plus" iconSize={IconSizes.SMALL} onClick={addStop} className={css.addButton}>
                    {translate(`${T}.ADD_COLOR`)}
                </Button>
            )}

            {mode === COLOR_MODES.BRACKETS &&
                dataClasses.map((c, i) => (
                    // Editable rows have no stable id; index keys are acceptable for this small list.
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={`bracket-${i}`} className={css.row}>
                        <div className={css.valueField}>
                            <TextField
                                placeholder={translate(`${T}.FROM_PLACEHOLDER`)}
                                value={c.from === undefined ? '' : String(c.from)}
                                onChange={v => updateClass(i, { from: v === '' ? undefined : Number(v) })}
                                inputConfig={{ type: 'number', step: 'any' }}
                            />
                        </div>
                        <div className={css.valueField}>
                            <TextField
                                placeholder={translate(`${T}.TO_PLACEHOLDER`)}
                                value={c.to === undefined ? '' : String(c.to)}
                                onChange={v => updateClass(i, { to: v === '' ? undefined : Number(v) })}
                                inputConfig={{ type: 'number', step: 'any' }}
                            />
                        </div>
                        <input
                            type="color"
                            value={c.color}
                            onChange={e => updateClass(i, { color: e.target.value })}
                            className={css.colorSwatch}
                        />
                        <div className={css.labelField}>
                            <TextField
                                placeholder={translate(`${T}.LABEL_PLACEHOLDER`)}
                                value={c.name || ''}
                                onChange={v => updateClass(i, { name: v })}
                            />
                        </div>
                        <Icon
                            name="trash"
                            size={IconSizes.SMALL}
                            variant={IconVariants.DARK}
                            onClick={() => removeClass(i)}
                            aria-label={translate(`${T}.REMOVE_BRACKET`)}
                        />
                    </div>
                ))}
            {mode === COLOR_MODES.BRACKETS && (
                <Button type="flat" Icon="plus" iconSize={IconSizes.SMALL} onClick={addClass} className={css.addButton}>
                    {translate(`${T}.ADD_BRACKET`)}
                </Button>
            )}
            {mode === COLOR_MODES.BRACKETS && (
                <div className={css.row}>
                    <div className={css.labelField}>
                        <Label text={`${T}.DEFAULT_COLOR`} />
                    </div>
                    <input
                        type="color"
                        className={css.colorSwatch}
                        value={options.defaultColor || HEATMAP_NO_MATCH_COLOR}
                        onChange={e => update({ defaultColor: e.target.value })}
                    />
                </div>
            )}
        </div>
    );
};

HeatmapColorConfig.propTypes = {
    value: PropTypes.shape({
        mode: PropTypes.string,
        showValues: PropTypes.bool,
        stops: PropTypes.arrayOf(PropTypes.shape({ stop: PropTypes.number, color: PropTypes.string })),
        dataClasses: PropTypes.arrayOf(
            PropTypes.shape({
                from: PropTypes.number,
                to: PropTypes.number,
                color: PropTypes.string,
                name: PropTypes.string,
            })
        ),
    }),
    onChange: PropTypes.func.isRequired,
    error: PropTypes.string,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(HeatmapColorConfig);
